// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Mirrors the old web build's lib/store.ts API so the UI logic ports cleanly, but
// every call now hits local SQLite via Tauri IPC (durable, offline, on-disk).

import { invoke } from "./tauri-web";

/**
 * The stored values stay `pending`/`approved`/`rejected` so existing ledgers and
 * backups keep working untouched — only what the person READS changed, because this
 * app is for someone tracking their own money, not a company approving a claim.
 * Labels live in STATUS_LABEL; never show the raw value.
 */
export type ExpenseStatus = "pending" | "approved" | "rejected";

/**
 * The three words a person reads for the three stored statuses.
 *
 * These are only DEFAULTS. Everyone sorts their money differently — "not mine" might be
 * "refunded", "business" or "disputed" depending on who you are — so the words are editable
 * (Categories panel) and kept on the device. The stored values never change, so a relabelled
 * ledger still exports, backs up and restores exactly the same.
 */
export const DEFAULT_STATUS_LABELS: Record<ExpenseStatus, string> = {
  pending: "to check",
  approved: "checked",
  rejected: "not mine",
};

const STATUS_LABELS_KEY = "oriora.expense.statusLabels";

export function loadStatusLabels(): Record<ExpenseStatus, string> {
  try {
    const raw = localStorage.getItem(STATUS_LABELS_KEY);
    if (!raw) return { ...DEFAULT_STATUS_LABELS };
    const saved = JSON.parse(raw) as Partial<Record<ExpenseStatus, string>>;
    const out = { ...DEFAULT_STATUS_LABELS };
    for (const k of Object.keys(out) as ExpenseStatus[]) {
      const v = typeof saved[k] === "string" ? saved[k]!.trim() : "";
      if (v) out[k] = v.slice(0, 24); // a chip, not an essay
    }
    return out;
  } catch {
    return { ...DEFAULT_STATUS_LABELS };
  }
}

export function saveStatusLabels(labels: Record<ExpenseStatus, string>): void {
  try {
    localStorage.setItem(STATUS_LABELS_KEY, JSON.stringify(labels));
  } catch {
    /* private mode / storage full — the defaults still render */
  }
  window.dispatchEvent(new Event("oriora-status-labels-changed"));
}

export interface Budget {
  id: string;
  /** null = an overall limit for the month, not tied to a category. */
  category_id: string | null;
  amount_cents: number;
  created_at: string;
  updated_at: string;
}

export interface BudgetStatus {
  id: string;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  amount_cents: number;
  spent_cents: number;
  /** Negative once they're over — the UI renders that as "over by". */
  remaining_cents: number;
  /** Clamped 0..100 for the bar width. */
  percent_used: number;
}

export interface MonthTotal {
  /** "YYYY-MM" */
  month: string;
  total_cents: number;
  count: number;
}

export interface Recurring {
  id: string;
  vendor: string;
  amount_cents: number;
  currency: string;
  category_id: string | null;
  notes: string | null;
  /** 'monthly' (day = 1..28 day-of-month) or 'weekly' (day = 0..6, Sunday=0). */
  frequency: "monthly" | "weekly";
  day: number;
  last_added_on: string | null;
  created_at: string;
}

export interface Category {
  id: string;
  name: string;
  color: string;
  is_default: boolean;
  created_at: string;
}

export interface Expense {
  id: string;
  category_id: string | null;
  vendor: string;
  amount_cents: number;
  currency: string;
  date_on: string;
  status: ExpenseStatus;
  notes: string | null;
  ai_confidence: number | null;
  receipt_path: string | null;
  created_at: string;
  updated_at: string;
  category_name: string | null;
  category_color: string | null;
}

export interface AuditEntry {
  id: string;
  expense_id: string | null;
  action: string;
  detail: string | null;
  at: string;
}

export interface CategorySummary {
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  total_cents: number;
  count: number;
}

export interface DashboardSummary {
  month_total_cents: number;
  month_count: number;
  by_category: CategorySummary[];
  recent: Expense[];
}

export interface NewExpense {
  vendor: string;
  amount_cents: number;
  date_on: string;
  category_id?: string | null;
  currency?: string;
  status?: ExpenseStatus;
  notes?: string | null;
  ai_confidence?: number | null;
  receipt_path?: string | null;
}

export type ExpensePatch = Partial<{
  vendor: string;
  amount_cents: number;
  currency: string;
  date_on: string;
  status: ExpenseStatus;
  category_id: string; // "" clears
  notes: string; // "" clears
}>;

// NL-query filter (applied locally over fetched rows; same shape the brain returns)
export interface ExpenseFilter {
  category?: string;
  status?: ExpenseStatus;
  from_date?: string;
  to_date?: string;
  min_cents?: number;
  max_cents?: number;
  vendor_contains?: string;
}

export const store = {
  ensureSeed: () => invoke<void>("ensure_seed"),
  listRecurring: () => invoke<Recurring[]>("list_recurring"),
  createRecurring: (input: { vendor: string; amount_cents: number; currency?: string | null; category_id?: string | null; notes?: string | null; frequency: "monthly" | "weekly"; day: number }) =>
    invoke<Recurring>("create_recurring", { vendor: input.vendor, amountCents: input.amount_cents, currency: input.currency ?? null, categoryId: input.category_id ?? null, notes: input.notes ?? null, frequency: input.frequency, day: input.day }),
  deleteRecurring: (id: string) => invoke<void>("delete_recurring", { id }),
  /** Create any due recurring expenses up to local-today; returns how many were added. */
  materializeRecurring: (today: string) => invoke<number>("materialize_recurring", { today }),
  listCategories: () => invoke<Category[]>("list_categories"),
  createCategory: (name: string, color: string) => invoke<Category>("create_category", { name, color }),
  updateCategory: (id: string, patch: { name?: string; color?: string }) =>
    invoke<Category>("update_category", { id, name: patch.name ?? null, color: patch.color ?? null }),
  deleteCategory: (id: string) => invoke<void>("delete_category", { id }),

  // Budgets + breakdown — on-device arithmetic, no AI call, nothing sent anywhere.
  setBudget: (categoryId: string | null, amountCents: number) =>
    invoke<Budget>("set_budget", { categoryId, amountCents }),
  deleteBudget: (id: string) => invoke<void>("delete_budget", { id }),
  budgetStatus: (monthStart: string) => invoke<BudgetStatus[]>("budget_status", { monthStart }),
  spendTrend: (months?: number) => invoke<MonthTotal[]>("spend_trend", { months: months ?? 6 }),
  spendByCategory: (from: string, to: string) =>
    invoke<CategorySummary[]>("spend_by_category", { from, to }),

  listExpenses: (opts?: { status?: string; categoryId?: string; limit?: number }) =>
    invoke<Expense[]>("list_expenses", { status: opts?.status, categoryId: opts?.categoryId, limit: opts?.limit ?? 50000 }),
  getExpense: (id: string) => invoke<Expense | null>("get_expense", { id }),
  createExpense: (input: NewExpense) => invoke<Expense>("create_expense", { input }),
  updateExpense: (id: string, patch: ExpensePatch) => invoke<void>("update_expense", { id, patch }),
  deleteExpense: (id: string) => invoke<void>("delete_expense", { id }),

  dashboardSummary: (monthStart: string) => invoke<DashboardSummary>("dashboard_summary", { monthStart }),
  listAudit: (expenseId?: string, limit?: number) => invoke<AuditEntry[]>("list_audit", { expenseId, limit: limit ?? 100 }),

  // copies a chosen image file into the app's receipts/ dir, returns the stored path
  saveReceiptImage: (srcPath: string) => invoke<string>("save_receipt_image", { srcPath }),
  // writes pasted clipboard image bytes into the app's receipts/ dir, returns the stored path
  savePastedImage: (bytes: number[], ext: string) => invoke<string>("save_pasted_image", { bytes, ext }),
};

// Apply a parsed NL filter locally over fetched rows (data never leaves the device).
export function applyFilter(expenses: Expense[], f: ExpenseFilter): Expense[] {
  return expenses.filter((e) => {
    if (f.category && (e.category_name ?? "").toLowerCase() !== f.category.toLowerCase()) return false;
    if (f.status && e.status !== f.status) return false;
    if (f.from_date && e.date_on < f.from_date) return false;
    if (f.to_date && e.date_on > f.to_date) return false;
    if (typeof f.min_cents === "number" && e.amount_cents < f.min_cents) return false;
    if (typeof f.max_cents === "number" && e.amount_cents > f.max_cents) return false;
    if (f.vendor_contains && !e.vendor.toLowerCase().includes(f.vendor_contains.toLowerCase())) return false;
    return true;
  });
}
